import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { workflowWarningDef, type WorkflowWarningKind } from '../lib/workflow-warnings';
import WorkflowWarningModal from '../components/WorkflowWarningModal';
import Tooltip from '../components/Tooltip';
import { ReasonBadge, TYPE_CONFIG, GroupHeader } from './ReviewQueue';
import { CheckCircle2, ArrowRight, ClipboardCheck, FileWarning, Lock } from 'lucide-react';

// Review upload subpage (2026-11-03 spec) — the file-centric attention list
// under Upload Document. Pending Review (/review-queue) is record-centric and
// cannot see files whose OCR never produced a record; this page lists FILES:
//   1. "Waiting for your review" — files whose imported statement/invoice is
//      held at needs_review (the review pages themselves do the confirming);
//   2. "Could not be read automatically" — files with no usable record at all
//      (OCR failed/unclear/type-mismatch/encrypted), otherwise invisible.
// What-to-do text comes from the workflow warning dictionary (same entries the
// Document Processing home uses), so guidance stays consistent across pages.

interface UploadReviewRecord {
  type: 'bank_statement' | 'card_statement' | 'invoice' | 'receipt';
  id: string;
  title: string;
  subtitle?: string;
  status: string;
  reason_flags: string[];
  reviewUrl: string;
}

interface UploadReviewItem {
  file_id: string;
  filename: string;
  original_name: string;
  folder: string;
  file_type: string;
  category: string;
  ocr_status: string;
  created_at: string;
  reason: 'record_needs_review' | 'ocr_failed' | 'ocr_unclear' | 'type_mismatch' | 'encrypted';
  reason_flags: string[];
  records: UploadReviewRecord[];
  review_url: string;
}

interface UploadReviewData {
  total: number;
  counts: { record_needs_review: number; ocr_unreadable: number };
  items_total: number;
  truncated: boolean;
  items: UploadReviewItem[];
}

// Record flags → warning-dictionary kinds (the dictionary owns the guidance
// text). Statements map verbatim; invoices take the invoice_ prefix the
// workflow summary uses; anything unmapped (or flag-less) is the generic hold.
const STMT_FLAG_KINDS: Record<string, WorkflowWarningKind> = {
  balance_mismatch: 'balance_mismatch',
  continuity_gap: 'continuity_gap',
  continuity_overlap: 'continuity_overlap',
  continuity_date_overlap: 'continuity_date_overlap',
  continuity_duplicate: 'continuity_duplicate',
  continuity_balance: 'continuity_balance',
  ocr_failed: 'ocr_failed',
};
const INV_FLAG_KINDS: Record<string, WorkflowWarningKind> = {
  total: 'invoice_total',
  direction: 'invoice_direction',
  duplicate: 'invoice_duplicate',
  new_company: 'invoice_new_company',
  invoice_number: 'invoice_invoice_number',
  company_not_detected: 'invoice_company_not_detected',
};
const FILE_REASON_KINDS: Record<string, WorkflowWarningKind> = {
  ocr_failed: 'file_ocr_failed',
  ocr_unclear: 'file_ocr_unclear',
  type_mismatch: 'file_ocr_type_mismatch',
  encrypted: 'file_ocr_encrypted',
};

function kindForItem(item: UploadReviewItem): WorkflowWarningKind {
  if (item.reason !== 'record_needs_review') return FILE_REASON_KINDS[item.reason] || 'file_ocr_failed';
  const first = item.records[0];
  if (!first) return 'review_pending';
  const table = first.type === 'invoice' || first.type === 'receipt' ? INV_FLAG_KINDS : STMT_FLAG_KINDS;
  for (const flag of item.reason_flags) {
    if (table[flag]) return table[flag];
  }
  return 'review_pending';
}

/** First action label of the mapped dictionary entry — the one-line what-to-do. */
function whatToDo(item: UploadReviewItem): string {
  const ctx = { targetId: item.records[0]?.id || item.file_id, reviewUrl: item.review_url };
  return workflowWarningDef(kindForItem(item), ctx).actions[0]?.label || '';
}

/** Modal context: file-level kinds take the FILE id (File Storage highlight),
 *  record kinds take the record id (review deep-link). */
function modalCtx(item: UploadReviewItem) {
  const rec = item.records[0];
  return {
    kind: kindForItem(item),
    targetId: item.reason === 'record_needs_review' && rec ? rec.id : item.file_id,
    detail: rec ? [rec.title, rec.subtitle].filter(Boolean).join(' · ') : item.folder,
    reviewUrl: item.review_url,
  };
}

function OcrStatusPill({ status }: { status: string }) {
  switch (status) {
    case 'failed':
      return <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full font-medium">{tr('OCR failed', 'OCR 失敗', 'OCR 失败')}</span>;
    case 'unclear':
      return <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">{tr('OCR unclear', 'OCR 不明', 'OCR 不明')}</span>;
    case 'type_mismatch':
      return <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">{tr('Type mismatch', '類型不符', '类型不符')}</span>;
    case 'encrypted':
      return <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium inline-flex items-center gap-1"><Lock className="h-2.5 w-2.5" />{tr('Encrypted', '已加密', '已加密')}</span>;
    default:
      return <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full font-medium">{status}</span>;
  }
}

export default function UploadReview() {
  const navigate = useNavigate();
  const [modalItem, setModalItem] = useState<UploadReviewItem | null>(null);

  const { data, isLoading } = useQuery<UploadReviewData>({
    queryKey: ['upload-review'],
    queryFn: () => api('/upload-review') as Promise<UploadReviewData>,
    refetchInterval: 5000,
  });

  const items = data?.items || [];
  const awaiting = items.filter(i => i.reason === 'record_needs_review');
  const unreadable = items.filter(i => i.reason !== 'record_needs_review');

  const detailsButton = (item: UploadReviewItem) => (
    <button
      onClick={e => { e.preventDefault(); e.stopPropagation(); setModalItem(item); }}
      className="text-xs text-primary hover:underline shrink-0 ml-3"
    >
      {tr('Details', '詳情', '详情')}
    </button>
  );

  const renderAwaitingItem = (item: UploadReviewItem) => {
    const rec = item.records[0];
    return (
      <a key={item.file_id} href={item.review_url}
        className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group">
        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium truncate">{item.filename}</span>
            <ReasonBadge reason={item.reason_flags.join(',')} />
            {item.records.length > 1 && (
              <Tooltip content={tr(
                `This file split into ${item.records.length} documents awaiting review.`,
                `此文件拆分成 ${item.records.length} 份待覆核文件。`,
                `此文件拆分成 ${item.records.length} 份待复核文件。`,
              )}>
                <span className="text-[10px] bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full font-medium cursor-help">
                  +{item.records.length - 1} {tr('more', '更多', '更多')}
                </span>
              </Tooltip>
            )}
          </div>
          {rec && (
            <p className="text-xs text-muted-foreground truncate">
              {[rec.title, rec.subtitle].filter(Boolean).join(' · ')}
            </p>
          )}
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span>{item.created_at?.slice(0, 10)}</span>
            <span className="text-muted-foreground/50">·</span>
            <span>{whatToDo(item)}</span>
          </div>
        </div>
        {detailsButton(item)}
        <span className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-3">
          <ArrowRight className="h-4 w-4" />
        </span>
      </a>
    );
  };

  const renderUnreadableItem = (item: UploadReviewItem) => (
    <a key={item.file_id} href={item.review_url}
      className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors group">
      <div className="space-y-0.5 min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium truncate">{item.filename}</span>
          <OcrStatusPill status={item.ocr_status} />
        </div>
        <p className="text-xs text-muted-foreground truncate">{item.folder}</p>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>{item.created_at?.slice(0, 10)}</span>
          <span className="text-muted-foreground/50">·</span>
          <span>{whatToDo(item)}</span>
        </div>
      </div>
      {detailsButton(item)}
      <span className="text-muted-foreground group-hover:text-primary transition-colors shrink-0 ml-3">
        <ArrowRight className="h-4 w-4" />
      </span>
    </a>
  );

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">{tr('Review Upload', '審核上傳', '审核上传')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {tr(
              'Uploaded files that need your attention — confirm the imports held for review, or fix the files OCR could not read.',
              '需要處理的上載文件——確認待覆核的匯入，或處理 OCR 無法讀取的文件。',
              '需要处理的上传文件——确认待复核的导入，或处理 OCR 无法读取的文件。'
            )}
          </p>
        </div>
        <span className="text-xs text-muted-foreground">{tr('Auto-refreshes every 5s', '每5秒自動更新', '每5秒自动更新')}</span>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-card border rounded-xl p-12 text-center space-y-3">
          <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
          <p className="text-lg font-semibold">{tr('All caught up!', '全部已完成！', '全部已完成！')}</p>
          <p className="text-sm text-muted-foreground">
            {tr('No uploaded files need attention.', '沒有需要處理的上載文件。', '没有需要处理的上传文件。')}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {data?.truncated && data.items_total != null && (
            <div className="text-xs text-amber-900 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 rounded px-3 py-2">
              {tr(
                `Showing the first ${items.length} of ${data.total} files.`,
                `僅顯示前 ${items.length} 個文件（共 ${data.total} 個）。`,
                `仅显示前 ${items.length} 个文件（共 ${data.total} 个）。`,
              )}
            </div>
          )}

          {/* ── Section 1: imports held for your confirmation ── */}
          {awaiting.length > 0 && (
            <div className="bg-card border rounded-xl overflow-hidden">
              <GroupHeader icon={ClipboardCheck} color="text-amber-600"
                title={tr('Waiting for your review', '待你覆核', '待你复核')}
                count={awaiting.length}
                tip={tr(
                  'These files were imported, but the statement or invoice they produced is held at needs review — its figures do not count in the books until you confirm the review.',
                  '這些文件已匯入，但其月結單或發票正待覆核——確認前其數字不會計入賬目。',
                  '这些文件已导入，但其月结单或发票正待复核——确认前其数字不会计入账目。'
                )} />
              {(() => {
                const grouped = new Map<string, UploadReviewItem[]>();
                for (const item of awaiting) {
                  const t = item.records[0]?.type || 'invoice';
                  if (!grouped.has(t)) grouped.set(t, []);
                  grouped.get(t)!.push(item);
                }
                return Array.from(grouped.entries()).map(([type, typeItems]) => {
                  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.invoice;
                  const Icon = cfg.icon;
                  return (
                    <div key={type} className="border-t first:border-t-0">
                      <div className="px-4 pt-2 flex items-center gap-1.5">
                        <Icon className={`h-3.5 w-3.5 ${cfg.color}`} />
                        <span className="text-xs font-semibold">{tr(cfg.label, cfg.labelZh, cfg.labelCn)}</span>
                      </div>
                      <div className="divide-y">{typeItems.map(renderAwaitingItem)}</div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

          {/* ── Section 2: files OCR could not turn into records ── */}
          {unreadable.length > 0 && (
            <div className="bg-card border rounded-xl overflow-hidden">
              <GroupHeader icon={FileWarning} color="text-red-500"
                title={tr('Could not be read automatically', '無法自動讀取', '无法自动读取')}
                count={unreadable.length}
                tip={tr(
                  'OCR could not produce a statement or invoice from these files. They are stored in File Storage but absent from the books until re-uploaded, unlocked, or entered manually.',
                  'OCR 未能從這些文件產生月結單或發票。文件已存於文件儲存，但在重新上載、解鎖或人手輸入前不會入賬。',
                  'OCR 未能从这些文件产生月结单或发票。文件已存于文件存储，但在重新上传、解锁或手动输入前不会入账。'
                )} />
              <div className="divide-y">{unreadable.map(renderUnreadableItem)}</div>
            </div>
          )}
        </div>
      )}

      {modalItem && (
        <WorkflowWarningModal
          {...modalCtx(modalItem)}
          onClose={() => setModalItem(null)}
          onNavigate={to => { setModalItem(null); navigate(to); }}
          onGapPrompt={() => setModalItem(null)}
        />
      )}
    </div>
  );
}
